import { EventEmitter, Injectable } from '@angular/core';
import { windowReferenceService } from './window-reference.service';
import SvgCanvas from '@svgeditor/svgcanvas.js';
import { SvgEditExtension } from '@models/svg-edit-extension';
import { transformPoint } from '@svgeditor/math.js';
import { WindowRef } from '@helpers/windowref';
import { Mimic } from 'icreate/common/models/mimic';
import * as roboto from '@src/assets/fonts/RobotoBase64';
import * as inter from '@src/assets/fonts/InterBase64';
import * as notosans from '@assets/fonts/NotosansRegBase64';

@Injectable({ providedIn: 'root' })
export class CanvasService {
    private _onChanged = new EventEmitter<any>();
    private _onGaugeAdded = new EventEmitter<any>();

    private canvas: SvgCanvas;

    constructor(
        private windowElements: windowReferenceService,
        private winRef: WindowRef
    ) {}

    init(): void {
        const container = this.windowElements.getEditorContainer();
        const { width, height } = { width: 0, height: 0 };

        const config = {
            initFill: { color: 'FFFFFF', opacity: 1 },
            initStroke: { color: '000000', opacity: 1, width: 1 },
            text: { stroke_width: 0, font_size: 16, font_family: 'serif' },
            initOpacity: 1,
            imgPath: '../src/editor/images',
            dimensions: [width, height],
            baseUnit: 'px',
        };

        this.canvas = new SvgCanvas(container, config);
        this.winRef.nativeWindow.canvas = this.canvas;

        this.prepCanvas();
        this.bindCanvasEvents();
    }

    private prepCanvas() {
        this.canvas.textActions.setInputElem(document.getElementById('text'));
        this.canvas.sanitizeSvg = () => {};
        (this.canvas as any)?.updateCanvas(0, 0);
        this.canvas.linkControlPoints(false);
    }

    private bindCanvasEvents() {
        this.canvas.bind('gauge_added', (win, elem) => this.emitGaugeAdded(elem));
        this.canvas.bind('changed', (win, elem) => this._onChanged.emit(elem));
        this.canvas.bind('inserted', (win, elem) => {
            if (Array.isArray(elem)) {
                this.canvas.selectOnly(elem, true);
            } else if (elem) {
                this.canvas.selectOnly([elem], true);
            }
        });
    }

    /**
     * @warn This function should not be called directly unless necessary for a function. Keep all features that use the canvas directly abstracted.
     * @returns a reference to SvgEdit's svgCanvas object
     */
    getCanvas(): SvgCanvas {
        return this.canvas;
    }

    onChanged() {
        return this._onChanged;
    }

    onGaugeAdded() {
        return this._onGaugeAdded;
    }

    emitGaugeAdded(gauge) {
        this._onGaugeAdded.emit(gauge);
    }

    toCanvasCoordinates(x: number, y: number) {
        const sctm = this.getSCTM();
        const pt = transformPoint(x, y, sctm);
        return pt;
    }

    getSCTM(): SVGMatrix {
        return (this.windowElements.getSvgContentElement().getElementsByTagName('g')[0] as any)
            .getScreenCTM()
            .inverse();
    }

    getSvgRoot() {
        return this.canvas.getSvgRoot();
    }

    getSvgContent() {
        return this.canvas.getSvgContent();
    }

    getNextId() {
        return this.canvas.getNextId();
    }

    addExtension(extension: SvgEditExtension, initArgs: any) {
        // Note that we don't want this on this.ready since some extensions
        // may want to run before then (like server_opensave).
        if (!this.canvas || this.canvas.extensions == undefined) {
            throw new Error('Extension added too early');
        }
        this.canvas.addExtension(extension.name, extension.init, initArgs);
    }

    transformPoint(x, y, m) {
        return {
            x: m.a * x + m.c * y + m.e,
            y: m.b * x + m.d * y + m.f,
        };
    }

    getStrokeColorForBackground(): string {
        const bgElement = document.getElementById('canvasBackground');
        if (!bgElement || !bgElement.children[0]) {
            return '#000000';
        }

        const fillAttr = bgElement.children[0].getAttribute('fill');
        if (!fillAttr) {
            return '#000000';
        }

        const color = this.hexToRgb(fillAttr);
        if (!color) {
            return '#000000';
        }

        const luminance = this.getRelativeLuminance(color.r, color.g, color.b);
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }

    private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
        if (!hex) return null;

        const colorNameMap = {
            white: '#FFFFFF',
            black: '#000000',
            red: '#FF0000',
            green: '#008000',
            blue: '#0000FF',
            gray: '#808080',
            grey: '#808080',
        };

        let hexValue = hex.toLowerCase().trim();

        if (colorNameMap[hexValue]) {
            hexValue = colorNameMap[hexValue];
        }

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})(?:[a-f\d]{2})?$/i.exec(hexValue);
        return result
            ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16),
              }
            : null;
    }

    private getRelativeLuminance(r: number, g: number, b: number): number {
        const [rs, gs, bs] = [r, g, b].map(c => {
            c = c / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    }

    getEmptySvg(mimic: Mimic) {
        return (
            '<svg id="' +
            mimic.name +
            '" width="' +
            mimic.profile.width +
            '" height="' +
            mimic.profile.height +
            '" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg">' +
            '<filter id="blur-filter" x="-3" y="-3" width="200" height="200"><feGaussianBlur in="SourceGraphic" stdDeviation="3" /></filter>' +
            '<g class="layer"><title>Layer 1</title></g></svg>'
        );
    }

    getMimicDefs() {
        return `  
            <defs>
                <style>
                    @font-face {
                        font-family: 'Inter';
                        src: url('data:application/font-woff;base64,${inter.base64}') format('woff');
                    }
                    @font-face {
                        font-family: 'Roboto-Regular';
                        src: url('data:application/font-woff;base64,${roboto.base64}') format('woff');
                    }
                    @font-face {
                        font-family: 'NotoSans';
                        src: url('data:application/font-woff;base64,${notosans.base64}') format('woff');
                    }
                </style>
            </defs>`;
    }
}
